import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
  type AttachmentAdapter,
  type PendingAttachment,
  type CompleteAttachment,
  type ThreadMessageLike,
} from "@assistant-ui/react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError, configDeModelo } from "@/lib/api/chatApi";
import type { ApiClient, Assistant } from "@/lib/api/chatApi";
import {
  datosContextoEnlace,
  envolverContextoEnlace,
  renderTemplate,
  textoArranqueEnlace,
  turnoDeEnlace,
  type LinkContext,
} from "@/lib/context";
import { readSSE, type SSECallbacks } from "./sse";

interface ChatRuntimeProps {
  api: ApiClient;
  assistantId: string;
  threadId?: string;
  agents: Assistant[];
  onChangeAgent: (agentId: string) => void;
  linkContext?: LinkContext | null;
  /** El agente ya recibio el contexto del enlace (el backend acepto el turno). */
  onLinkContextConsumed?: () => void;
  /** El turno que lo llevaba fallo o se bloqueo: vuelve a quedar pendiente. */
  onLinkContextRestored?: (linkContext: LinkContext) => void;
  /** Avisa del hilo de esta conversacion (tambien del que nace en el primer turno). */
  onThreadIdChange?: (threadId: string | null) => void;
  children: ReactNode;
}

/* ── Shared chat state (minimal — only what assistant-ui can't handle) ── */

export interface TokenUsage {
  input: number;
  output: number;
  cached: number;
  cost: number;
}

interface ChatState {
  threadId: string | null;
  traceIds: (string | null)[];
  ratings: Record<number, "ok" | "ko">;
  setRating: (index: number, value: "ok" | "ko") => void;
  api: ApiClient;
  assistantId: string;
  agents: Assistant[];
  onChangeAgent: (agentId: string) => void;
  tokenUsage: TokenUsage;
  attachmentError: string | null;
  clearAttachmentError: () => void;
  /** `model_override` del thread, o `null` si corre con el default. */
  modeloElegido: string | null;
  elegirModelo: (modelId: string | null) => void;
  /** El modelo elegido no llego al turno porque fue por el camino de upload. */
  avisoModeloAdjunto: boolean;
  limpiarAvisoModeloAdjunto: () => void;
}

const ChatCtx = createContext<ChatState | null>(null);

// Context hook co-located with its provider component; splitting it to its own
// file only to satisfy fast-refresh isn't worth the import churn.
// eslint-disable-next-line react-refresh/only-export-components
export function useChatState() {
  const ctx = useContext(ChatCtx);
  if (!ctx) throw new Error("useChatState must be inside ChatRuntime");
  return ctx;
}

/* ── Attachment adapter (official pattern) ── */

const fileAttachmentAdapter: AttachmentAdapter = {
  accept: ".pdf,.png,.jpg,.jpeg,.docx,.xlsx,.csv,.txt,.tsv,.json,.xml,.n43,.q43,image/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,text/tab-separated-values,application/json,text/xml",

  async add({ file }) {
    return {
      id: crypto.randomUUID(),
      type: "document" as const,
      name: file.name,
      contentType: file.type,
      file,
      status: { type: "requires-action" as const, reason: "composer-send" as const },
    };
  },

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    return {
      id: attachment.id,
      type: "document" as const,
      name: attachment.name,
      status: { type: "complete" as const },
      content: [{ type: "text" as const, text: attachment.name }],
    } as CompleteAttachment;
  },

  async remove() {
    // Nothing to clean up — file is not uploaded to storage
  },
};


/* ── Component ── */

export default function ChatRuntime({ api, assistantId, threadId: existingThreadId, agents, onChangeAgent, linkContext, onLinkContextConsumed, onLinkContextRestored, onThreadIdChange, children }: ChatRuntimeProps) {
  const { t } = useTranslation();
  const [threadId, setThreadId] = useState<string | null>(existingThreadId ?? null);
  const onThreadIdChangeRef = useRef(onThreadIdChange);
  onThreadIdChangeRef.current = onThreadIdChange;
  useEffect(() => { onThreadIdChangeRef.current?.(threadId); }, [threadId]);

  // The agent actually running this conversation. Starts as the prop (e.g. "auto"
  // for a brand-new chat) and is replaced by the real agent once the backend emits
  // `event: routed`. Exposed via useChatState so the UI shows the resolved agent and
  // history reloads target the right graph. The request itself still sends `assistantId`.
  const [resolvedAssistantId, setResolvedAssistantId] = useState(assistantId);
  // Keep in sync when the prop changes (e.g. user picks an agent or reopens a thread)
  useEffect(() => { setResolvedAssistantId(assistantId); }, [assistantId]);
  // For history reload we need a concrete graph id; never "auto"
  const historyAssistantId = resolvedAssistantId === "auto" ? assistantId : resolvedAssistantId;
  const [traceIds, setTraceIds] = useState<(string | null)[]>([]);
  const [ratings, setRatings] = useState<Record<number, "ok" | "ko">>({});
  const setRating = (index: number, value: "ok" | "ko") => setRatings((prev) => ({ ...prev, [index]: value }));
  const threadIdRef = useRef(existingThreadId ?? null);

  // Store link context in a ref so the adapter closure can always read the latest value
  const linkContextRef = useRef(linkContext ?? null);
  linkContextRef.current = linkContext ?? null;
  const onLinkContextConsumedRef = useRef(onLinkContextConsumed);
  onLinkContextConsumedRef.current = onLinkContextConsumed;
  const onLinkContextRestoredRef = useRef(onLinkContextRestored);
  onLinkContextRestoredRef.current = onLinkContextRestored;

  const [initialMessages, setInitialMessages] = useState<
    ThreadMessageLike[] | undefined
  >(existingThreadId ? undefined : []);

  useEffect(() => {
    if (!existingThreadId) return;
    api.getThreadMessages(existingThreadId, historyAssistantId)
      .then((msgs) => setInitialMessages(msgs))
      .catch((err) => {
        console.error("Failed to load thread history:", err);
        setInitialMessages([]);
      });
  }, [existingThreadId, historyAssistantId, api]);

  const [tokenUsage, setTokenUsage] = useState<TokenUsage>({ input: 0, output: 0, cached: 0, cost: 0 });
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const clearAttachmentError = () => setAttachmentError(null);

  /* ── Modelo de la conversacion (solo admins) ── */

  // `null` = corre con el modelo por defecto de su agente.
  const [modeloElegido, setModeloElegido] = useState<string | null>(null);
  // Override todavia NO estrenado: el backend solo lo guarda cuando llega un
  // turno con `config`. Mientras siga pendiente, el camino de upload (multipart,
  // sin `config`) correria con el default sin avisar — ver `avisoModeloAdjunto`.
  const modeloPendienteRef = useRef<string | null>(null);

  // Hidratacion al ABRIR una conversacion existente: el backend RELEE el
  // override guardado aunque el front no lo mande, asi que sin esto el selector
  // diria "por defecto" mientras la conversacion corre con otro modelo.
  useEffect(() => {
    if (!existingThreadId) {
      setModeloElegido(null);
      modeloPendienteRef.current = null;
      return;
    }
    let cancelado = false;
    api.getThread(existingThreadId)
      .then((meta) => {
        if (cancelado) return;
        setModeloElegido(meta.model_override ?? null);
        // Lo guardado NO esta pendiente: el backend ya lo hereda solo.
        modeloPendienteRef.current = null;
      })
      // Fail-safe: sin este dato el selector muestra el default. Es el
      // comportamiento de antes de existir, y no vale tumbar el chat por el.
      .catch((err) => console.error("Failed to load thread model:", err));
    return () => { cancelado = true; };
  }, [existingThreadId, api]);

  const elegirModelo = (modelId: string | null) => {
    setModeloElegido(modelId);
    // OJO con el `null`: el backend guarda el override con
    // `COALESCE(EXCLUDED.model_override, thread_metadata.model_override)`, asi
    // que mandar NULL **no borra** el guardado — es justo lo que hace que un
    // turno normal no pierda el override. No hay hoy forma de volver al default
    // en una conversacion que ya fijo uno; por eso el selector deshabilita esa
    // opcion una vez hay override guardado (ver `bloqueado` en SelectorModelo)
    // y aqui solo se marca pendiente una eleccion REAL.
    modeloPendienteRef.current = modelId;
  };

  // Aviso de que el modelo recien elegido no viaja por el camino de upload. Se
  // pinta cuando hay eleccion sin estrenar Y el usuario tiene archivos puestos.
  const [avisoModeloAdjunto, setAvisoModeloAdjunto] = useState(false);

  // Reset usage and trace IDs when thread changes
  useEffect(() => {
    setTokenUsage({ input: 0, output: 0, cached: 0, cost: 0 });
    setTraceIds([]);
    setRatings({});
  }, [threadId]);

  // Index of the current run's traceId slot (set before streaming starts)
  const currentTraceIdxRef = useRef(-1);

  const sseCallbacks: SSECallbacks = {
    onEnd: (data) => {
      if (data.thread_id) {
        threadIdRef.current = data.thread_id;
        setThreadId(data.thread_id);
      }
      // Update the pre-allocated slot with the real trace_id
      const idx = currentTraceIdxRef.current;
      if (idx >= 0) {
        setTraceIds((prev) => {
          const next = [...prev];
          next[idx] = data.trace_id ?? null;
          return next;
        });
      }
    },
    onUsage: (data) => {
      setTokenUsage((prev) => ({
        input: prev.input + (data.input || 0),
        output: prev.output + (data.output || 0),
        cached: prev.cached + (data.cached || 0),
        cost: prev.cost + (data.cost || 0),
      }));
    },
    onRouted: (id) => setResolvedAssistantId(id),
    clarifyMessage: (candidates) => {
      const lines = candidates.map((c) => `- ${c.label}`).join("\n");
      return `${t("chat.clarifyIntro")}\n${lines}\n${t("chat.clarifyAsk")}`;
    },
    errorMessage: t("chat.streamError"),
  };

  // Store pending files from attachment adapter for use in run()
  // useRef so it persists across renders (add() and run() happen in different render cycles)
  const pendingFilesRef = useRef(new Map<string, File>());

  const myAttachmentAdapter: AttachmentAdapter = {
    accept: fileAttachmentAdapter.accept,
    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      const content: { type: string; text?: string; image?: string }[] = [
        { type: "text", text: attachment.name },
      ];
      // Preserve image preview URL for immediate display after send
      if (attachment.file?.type?.startsWith("image/")) {
        const url = URL.createObjectURL(attachment.file);
        content.push({ type: "image", image: url });
      }
      return {
        id: attachment.id,
        type: "document" as const,
        name: attachment.name,
        contentType: attachment.contentType,
        status: { type: "complete" as const },
        content,
      } as CompleteAttachment;
    },
    add({ file }) {
      // Enforce max 5 files per message
      if (pendingFilesRef.current.size >= 5) {
        setAttachmentError(t("chat.maxFilesReached", { max: 5 }));
        return Promise.reject(new Error("max_files"));
      }
      // Reject legacy office formats (.doc / .xls)
      const legacyTypes = ["application/msword", "application/vnd.ms-excel"];
      const legacyExts = [".doc", ".xls"];
      const isLegacy =
        legacyTypes.includes(file.type) ||
        legacyExts.some((ext) => file.name.toLowerCase().endsWith(ext));
      if (isLegacy) {
        setAttachmentError(t("chat.errorLegacyFormat"));
        return Promise.reject(new Error("legacy_format"));
      }
      // Un extracto Norma 43 va a S3 (no se inlinea), así que NO se le aplica el tope
      // de 50 KB de los ficheros de texto: los extractos reales pesan más. Se reconoce
      // por extensión (.n43/.q43) o porque el agente activo es el conciliador (cualquier
      // fichero que le traigas es un extracto). Se le aplica el tope de 32 MB (como office).
      const nombre = file.name.toLowerCase();
      const esNorma43 =
        nombre.endsWith(".n43") ||
        nombre.endsWith(".q43") ||
        resolvedAssistantId === "conciliador_norma_43";
      // Enforce 50 KB limit for text files (excepto extractos Norma 43)
      const textTypes = ["text/csv", "text/plain", "text/tab-separated-values", "application/json", "text/xml"];
      if (!esNorma43 && textTypes.includes(file.type) && file.size > 50 * 1024) {
        setAttachmentError(t("chat.errorTooLarge"));
        return Promise.reject(new Error("file_too_large"));
      }
      if (esNorma43 && file.size > 32 * 1024 * 1024) {
        setAttachmentError(t("chat.errorTooLarge"));
        return Promise.reject(new Error("file_too_large"));
      }
      // Enforce 32 MB limit for office files (.docx, .xlsx)
      const officeTypes = [
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (officeTypes.includes(file.type) && file.size > 32 * 1024 * 1024) {
        setAttachmentError(t("chat.errorTooLarge"));
        return Promise.reject(new Error("file_too_large"));
      }
      const id = crypto.randomUUID();
      pendingFilesRef.current.set(id, file);
      return Promise.resolve({
        id,
        type: "document" as const,
        name: file.name,
        contentType: file.type,
        file,
        status: { type: "requires-action" as const, reason: "composer-send" as const },
      });
    },
    remove(attachment) {
      pendingFilesRef.current.delete(attachment.id);
      return Promise.resolve();
    },
  };

  const adapter: ChatModelAdapter = {
    async *run({ messages, abortSignal }) {
      // Pre-allocate a traceId slot so ratings show immediately after streaming.
      // The onEnd callback will fill it with the real trace_id if it arrives.
      setTraceIds((prev) => {
        currentTraceIdxRef.current = prev.length;
        return [...prev, null];
      });
      const lastUserMsg = messages.filter((m) => m.role === "user").pop();
      const userText = lastUserMsg
        ? typeof lastUserMsg.content === "string"
          ? lastUserMsg.content
          : lastUserMsg.content.map((p) => (p.type === "text" ? p.text : "")).join("")
        : "";

      // Collect ALL pending files (not just the first one)
      const pendingFiles = [...pendingFilesRef.current.values()];
      pendingFilesRef.current.clear();

      // Enlace directo con contexto aun sin entregar: la plantilla del widget,
      // renderizada y envuelta en sus marcas (envolverContextoEnlace: el historial
      // las quita al recargar), se antepone al mensaje humano de este turno.
      const linkCtx = linkContextRef.current;
      const contextoRenderizado = linkCtx
        ? renderTemplate(linkCtx.contextPromptTemplate, linkCtx.context)
        : "";
      const prefijoContextoEnlace = contextoRenderizado ? envolverContextoEnlace(contextoRenderizado) : null;
      // Solo cuenta como enlace si hay contexto que entregar.
      const turnoEnlace = turnoDeEnlace(prefijoContextoEnlace ? linkCtx : null, {
        consumir: () => {
          linkContextRef.current = null;
          onLinkContextConsumedRef.current?.();
        },
        restaurar: (link) => {
          linkContextRef.current = link;
          onLinkContextRestoredRef.current?.(link);
        },
      });
      // Callbacks de ESTE turno: los comunes mas los que siguen al enlace.
      const callbacksTurno: SSECallbacks = {
        ...sseCallbacks,
        onMetadata: (data) => {
          if (!data.thread_id) return;
          // El hilo existe desde aqui: si el stream se corta antes de `end`, el
          // siguiente turno sigue en el mismo en vez de abrir otro.
          if (!threadIdRef.current) {
            threadIdRef.current = data.thread_id;
            setThreadId(data.thread_id);
          }
          turnoEnlace.guardarHilo(data.thread_id);
        },
        onBlocked: () => turnoEnlace.fallo(),
        onError: () => turnoEnlace.fallo(),
      };

      if (pendingFiles.length > 0) {
        // Upload path — send files to backend with SSE streaming
        let msgText = userText || "Procesa este pedido.";
        if (prefijoContextoEnlace) msgText = prefijoContextoEnlace + "\n\n" + msgText;

        try {
          const response = await api.uploadFiles({
            files: pendingFiles,
            assistantId,
            message: msgText,
            threadId: threadIdRef.current ?? undefined,
          });
          turnoEnlace.enviado();
          // El multipart NO lleva `config`, asi que un modelo recien elegido y
          // aun no estrenado se PIERDE en este camino: el backend solo puede
          // heredar el ya guardado. Se avisa en vez de callar — si no, el admin
          // sacaria conclusiones sobre un modelo con el que el turno no corrio.
          //
          // DESPUES de la respuesta, no antes: un 409 (`threadBusy`) aborta el
          // turno sin llegar a correr, y avisar ahi diria "el modelo no se
          // aplico" sobre algo que no paso — que es justo el tipo de conclusion
          // equivocada que este aviso existe para evitar.
          if (modeloPendienteRef.current !== null) {
            setAvisoModeloAdjunto(true);
          }
          if (!response.body) throw new Error("No response body");
          yield* readSSE(response.body, callbacksTurno, abortSignal);
        } catch (err) {
          turnoEnlace.fallo();
          if (err instanceof ApiError && err.status === 409) {
            yield { content: [{ type: "text" as const, text: t("chat.threadBusy") }] };
            return;
          }
          throw err;
        }
      } else {
        // Text streaming path.
        // In auto mode we DON'T pre-create the thread: the backend routes the
        // message and generates the thread_id, which arrives via metadata/end
        // (handled in onEnd). For concrete agents we keep creating it upfront so
        // the thread_id is known before streaming.
        let currentThreadId = threadIdRef.current;
        if (!currentThreadId && assistantId !== "auto") {
          try {
            const { thread_id } = await api.createThread();
            currentThreadId = thread_id;
          } catch (err) {
            turnoEnlace.fallo();
            throw err;
          }
          threadIdRef.current = currentThreadId;
          setThreadId(currentThreadId);
          turnoEnlace.guardarHilo(currentThreadId);
        }

        const lastMsg = messages.filter((m) => m.role === "user").pop();
        let userContent: string | { type: string; text: string }[] = "";
        if (lastMsg) {
          userContent = typeof lastMsg.content === "string"
            ? lastMsg.content
            : lastMsg.content.map((p) =>
                p.type === "text"
                  ? { type: "text" as const, text: p.text }
                  : { type: "text" as const, text: "" },
              );
        }

        // Prepend link context to user message (like RAG injection)
        if (prefijoContextoEnlace && typeof userContent === "string") {
          userContent = prefijoContextoEnlace + "\n\n" + userContent;
        } else if (prefijoContextoEnlace && Array.isArray(userContent)) {
          userContent = [{ type: "text" as const, text: prefijoContextoEnlace + "\n\n" }, ...userContent];
        }

        const langchainMessages = [{ type: "human" as const, content: userContent }];

        try {
          // El override viaja SOLO cuando esta sin estrenar, no en cada turno:
          // el backend relee el guardado del thread por su cuenta. Mandarlo
          // siempre pagaria un SELECT contra el catalogo por turno —que ese
          // diseño existe para evitar— y meteria un 503 posible en turnos que
          // habrian ido bien.
          const configModelo = configDeModelo(modeloPendienteRef.current);
          const response = await api.sendMessage({
            assistantId,
            threadId: currentThreadId ?? undefined,
            input: { messages: langchainMessages },
            streamMode: ["messages", "updates"],
            ...(configModelo ? { config: configModelo } : {}),
          });
          // Estrenado: a partir de aqui lo hereda el thread. Se limpia DESPUES
          // de que la peticion salga, para que un fallo de red no lo de por
          // enviado y el siguiente turno vuelva al default en silencio.
          modeloPendienteRef.current = null;
          turnoEnlace.enviado();
          if (!response.body) throw new Error("No response body");
          yield* readSSE(response.body, callbacksTurno, abortSignal);
        } catch (err) {
          turnoEnlace.fallo();
          if (err instanceof ApiError && err.status === 409) {
            yield { content: [{ type: "text" as const, text: t("chat.threadBusy") }] };
            return;
          }
          throw err;
        }
      }
    },
  };

  const runtime = useLocalRuntime(adapter, {
    initialMessages: initialMessages ?? [],
    adapters: { attachments: myAttachmentAdapter },
  });

  // Enlace directo: la conversacion arranca sola con un primer mensaje que dice
  // con que datos llega ("Ponme en contexto: Caso WISE 4434 · Cliente 0001123"),
  // al que el adaptador antepone la plantilla del widget. Asi el agente contesta
  // con un resumen y el usuario ve que esta al tanto; sin esto, el chat de un
  // enlace era identico a la home y parecia que el enlace no habia hecho nada.
  // Una sola vez por montaje (el ref sobrevive al doble efecto de StrictMode).
  // Solo si el widget lo pide (`auto_start`): cuesta un turno del agente por
  // enlace nuevo. Sin el, el contexto espera al primer mensaje del usuario.
  const arranqueEnlaceRef = useRef(false);
  useEffect(() => {
    if (arranqueEnlaceRef.current || existingThreadId || !linkContext?.autoStart) return;
    const datos = datosContextoEnlace(linkContext);
    if (!datos) return;
    arranqueEnlaceRef.current = true;
    runtime.thread.append({
      role: "user",
      content: [{ type: "text", text: textoArranqueEnlace(datos, t) }],
    });
  }, [runtime, existingThreadId, linkContext, t]);

  if (initialMessages === undefined) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" />
      </div>
    );
  }

  return (
    <ChatCtx.Provider
      value={{
        threadId,
        traceIds,
        ratings,
        setRating,
        api,
        assistantId: resolvedAssistantId,
        agents,
        onChangeAgent,
        tokenUsage,
        attachmentError,
        clearAttachmentError,
        modeloElegido,
        elegirModelo,
        avisoModeloAdjunto,
        limpiarAvisoModeloAdjunto: () => setAvisoModeloAdjunto(false),
      }}
    >
      <AssistantRuntimeProvider runtime={runtime}>
        {children}
      </AssistantRuntimeProvider>
    </ChatCtx.Provider>
  );
}
